import { PayloadAction, createSlice, isRejected, isPending, isFulfilled } from '@reduxjs/toolkit';
import { AUTH_SLICE_NAME, AuthState, initialState } from './models';
import { appCookiesStorage, showApiErrors } from '@/utils';
import { forgotPassword, logOut, resetPassword, signIn, signInVerify, changePassword } from './actionCreators';
import { AUTHORIZATION_TOKEN_STORAGE_KEY, REFRESH_TOKEN_STORAGE_KEY } from '@/constants/common';
import { showErrorToast } from '@/components';
import { AuthResponseErrors } from '../helpers/constants';

export const slice = createSlice({
  name: AUTH_SLICE_NAME,
  initialState,
  reducers: {
    setIsAuthorized(state, action: PayloadAction<boolean>) {
      state.isAuthorized = action.payload;
    },
    setIsLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },
    resetAuthState() {
      return { ...initialState };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(logOut.fulfilled, () => {
        appCookiesStorage.removeAll();
        localStorage.clear();
        return { ...initialState, isAuthorized: false, isLoading: false };
      })
      .addCase(logOut.rejected, () => {
        appCookiesStorage.removeAll();
        localStorage.clear();
        return { ...initialState, isAuthorized: false, isLoading: false };
      })
      .addCase(signIn.fulfilled, (state: AuthState, action) => {
        state.isLoading = false;
        state.currentAction = action.payload.action;
        state.authSessionId = action.payload.session;
        state.setupMfaQrCode = action.payload.qrCode ?? null;
        state.setupMfaSecretCode = action.payload.secretCode ?? null;
      })
      .addCase(signIn.rejected, (state: AuthState, action) => {
        const { error } = action;
        state.isLoading = false;
        state.error = error;

        if (error?.message === AuthResponseErrors.USER_DISABLED) {
          showErrorToast({ message: 'You can’t log in into the system, because your account was deactivated.' });
        } else {
          showApiErrors(error);
        }
      })
      .addCase(signInVerify.fulfilled, (state: AuthState, action) => {
        state.isLoading = false;
        const { payload } = action;

        if (payload.qrCode) {
          state.currentAction = payload.action;
          state.authSessionId = payload.session;
          state.setupMfaQrCode = payload.qrCode;
          state.setupMfaSecretCode = payload.secretCode ?? null;
        }

        if (payload.idToken) {
          state.isAuthorized = true;
          appCookiesStorage.setItem(AUTHORIZATION_TOKEN_STORAGE_KEY, payload.idToken);
          appCookiesStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, payload.refreshToken);
        }
      })
      .addMatcher(
        isPending(signIn, signInVerify, resetPassword, forgotPassword, changePassword),
        (state: AuthState) => {
          state.isLoading = true;
        },
      )
      .addMatcher(
        isFulfilled(signIn, signInVerify, resetPassword, forgotPassword, changePassword),
        (state: AuthState) => {
          state.isLoading = false;
        },
      )
      .addMatcher(
        isRejected(signInVerify, resetPassword, forgotPassword, changePassword),
        (state: AuthState, action) => {
          const { error } = action;
          state.isLoading = false;
          showApiErrors(error);
        },
      );
  },
});

export const { setIsAuthorized, setIsLoading, resetAuthState } = slice.actions;

export default slice.reducer;
